/**
 * AndamentoCard — le ultime 5 partite della mia squadra, a colpo d'occhio.
 *
 * Andrea 2026-09-09: sulla home, fra "La mia squadra" e "Prossima sfida",
 * un blocco diviso in due metà — a sinistra il campionato, a destra la
 * Champions — con i quadratini V (verde) / P (giallo) / S (rosso). Se le
 * partite giocate sono meno di 5, i posti mancanti restano grigi con un
 * trattino.
 *
 * Andrea 2026-09-10: ogni metà porta al calendario della SUA competizione
 * (?competizione=<id>), non piu' al calendario generico.
 */

import { Link } from "react-router-dom";
import {
  usePartiteCompetizione,
  type CompetizioneItem,
  type PartitaItem,
} from "../../hooks/useFase5";
import { tr } from "../../i18n";

const N_PARTITE = 5;

type Esito = "vittoria" | "pareggio" | "sconfitta";

interface Risultato {
  esito: Esito;
  descrizione: string;
}

interface AndamentoCardProps {
  squadraId: string;
  /** Giornate della stagione: servono solo per ordinare le partite. */
  giornate: Array<{ id: string; numero: number }>;
  competizioni: CompetizioneItem[];
  nomeSquadra: (id: string) => string;
}

const STILE_ESITO: Record<Esito, { lettera: string; cls: string }> = {
  vittoria: { lettera: "V", cls: "bg-success text-white border-success" },
  pareggio: { lettera: "P", cls: "bg-warning text-ink border-warning" },
  sconfitta: { lettera: "S", cls: "bg-danger text-white border-danger" },
};

/** Le ultime N partite già giocate della squadra, dalla meno alla più
 * recente. Una partita conta come giocata quando ha il punteggio. */
function ultimiRisultati(
  partite: PartitaItem[] | undefined,
  squadraId: string,
  numeroGiornata: Map<string, number>,
  nomeSquadra: (id: string) => string,
): Risultato[] {
  const mie = (partite ?? [])
    .filter(
      (p) =>
        !p.obsoleto &&
        p.punteggio_casa !== null &&
        p.punteggio_casa !== undefined &&
        (p.squadra_casa_id === squadraId || p.squadra_trasferta_id === squadraId),
    )
    .sort(
      (a, b) =>
        (numeroGiornata.get(a.giornata_id ?? "") ?? 0) -
        (numeroGiornata.get(b.giornata_id ?? "") ?? 0),
    );
  return mie.slice(-N_PARTITE).map((p) => {
    const inCasa = p.squadra_casa_id === squadraId;
    const miei = inCasa ? p.gol_casa : p.gol_trasferta;
    const loro = inCasa ? p.gol_trasferta : p.gol_casa;
    const esito: Esito = miei > loro ? "vittoria" : miei < loro ? "sconfitta" : "pareggio";
    const giornata = numeroGiornata.get(p.giornata_id ?? "");
    const testa = giornata ? tr("Giornata {{n}}", { n: giornata }) : tr("Partita");
    return {
      esito,
      descrizione: `${testa}: ${nomeSquadra(p.squadra_casa_id)} ${p.gol_casa}-${
        p.gol_trasferta
      } ${nomeSquadra(p.squadra_trasferta_id)}`,
    };
  });
}

function Quadratini({ risultati }: { risultati: Risultato[] }) {
  const vuoti = Math.max(0, N_PARTITE - risultati.length);
  return (
    // Andrea 2026-09-10: su desktop i quadratini stanno piu' larghi e
    // centrati (su mobile restano compatti a sinistra, ci sta giusto).
    <div className="flex items-center gap-1 md:gap-3 md:justify-center">
      {risultati.map((r, i) => {
        const stile = STILE_ESITO[r.esito];
        return (
          <span
            key={i}
            title={r.descrizione}
            className={`w-6 h-6 sm:w-7 sm:h-7 rounded border font-display text-sm flex items-center justify-center ${stile.cls}`}
          >
            {stile.lettera}
          </span>
        );
      })}
      {Array.from({ length: vuoti }, (_, i) => (
        <span
          key={`vuoto-${i}`}
          title={tr("Partita non ancora giocata")}
          className="w-6 h-6 sm:w-7 sm:h-7 rounded border border-fg-muted/30 bg-fg-muted/10 text-fg-muted font-display text-sm flex items-center justify-center"
        >
          –
        </span>
      ))}
    </div>
  );
}

export function AndamentoCard({
  squadraId,
  giornate,
  competizioni,
  nomeSquadra,
}: AndamentoCardProps) {
  const campionato = competizioni.find((c) => c.tipo === "campionato") ?? null;
  const champions = competizioni.find((c) => c.tipo === "champions_league") ?? null;

  const { data: partiteCampionato } = usePartiteCompetizione(campionato?.id ?? null);
  const { data: partiteChampions } = usePartiteCompetizione(champions?.id ?? null);

  const numeroGiornata = new Map(giornate.map((g) => [g.id, g.numero]));
  const meta: Array<{
    titolo: string;
    competizioneId: string | null;
    risultati: Risultato[];
  }> = [
    {
      titolo: campionato?.nome ?? tr("Campionato"),
      competizioneId: campionato?.id ?? null,
      risultati: ultimiRisultati(partiteCampionato, squadraId, numeroGiornata, nomeSquadra),
    },
    {
      titolo: champions?.nome ?? tr("Coppa"),
      competizioneId: champions?.id ?? null,
      risultati: ultimiRisultati(partiteChampions, squadraId, numeroGiornata, nomeSquadra),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-baseline mb-2">
        <h3 className="font-display text-lg tracking-wider text-fg">{tr("ANDAMENTO")}</h3>
        <span className="text-[10px] text-fg-muted uppercase font-mono">ultime {N_PARTITE}</span>
      </div>
      <div className="grid grid-cols-2 divide-x divide-primary/20 bg-bg-card border border-primary/20 rounded-card overflow-hidden">
        {meta.map((m) => {
          const contenuto = (
            <>
              <div className="font-mono text-[10px] uppercase tracking-wider text-fg-muted truncate mb-2">
                {m.titolo}
              </div>
              <Quadratini risultati={m.risultati} />
            </>
          );
          // Ogni metà apre il calendario della sua competizione.
          if (!m.competizioneId) {
            return (
              <div key={m.titolo} className="p-3 min-w-0 opacity-60 md:text-center">
                {contenuto}
              </div>
            );
          }
          return (
            <Link
              key={m.titolo}
              to={`/giornata?competizione=${m.competizioneId}`}
              className="p-3 min-w-0 hover:bg-primary-deep/40 transition md:text-center"
              title={tr("Apri il calendario · {{titolo}}", { titolo: m.titolo })}
            >
              {contenuto}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
